import axiosInstance from '../apiServices/axios';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { RAID_ITEMS, PROJECT_RAID_OWNERS, RAID_ITEM_COMMENTS } from '../apiServices/api_routes';
import { toast } from 'react-toastify';
import {
    buildProjectCsvExportFilename,
    buildProjectCsvTemplateFilename,
} from '../utils/exportPdfFilename';

export const GetRaidItems = createAsyncThunk('GET_RAID_ITEMS', async () => {
    const response = await axiosInstance.get(RAID_ITEMS + localStorage['project_id'] + '/');
    return response;
});

export const PostRaidItem = createAsyncThunk('POST_RAID_ITEM', async (body, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(RAID_ITEMS + localStorage['project_id'] + '/', body);
        return response;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const DownloadRaidTemplate = createAsyncThunk('RAID_DOWNLOAD_CSV_TEMPLATE', async (_, { rejectWithValue, getState }) => {
    try {
        const response = await axiosInstance.get(RAID_ITEMS + localStorage['project_id'] + '/download_template/', {
            responseType: 'blob',
        });
        return {
            data: response.data,
            filename: buildProjectCsvTemplateFilename(getState, 'RAID'),
        };
    } catch (error) {
        return rejectWithValue(error.response?.data || 'Download failed');
    }
});

export const ExportRaidCSV = createAsyncThunk('RAID_EXPORT_CSV', async (raidItemIds = null, { rejectWithValue, getState }) => {
    try {
        let url = RAID_ITEMS + localStorage['project_id'] + '/export_csv/';
        if (raidItemIds && raidItemIds.length > 0) {
            // Add raid_item_ids as query parameter
            const raidItemIdsParam = raidItemIds.join(',');
            url += `?raid_item_ids=${raidItemIdsParam}`;
        }
        const response = await axiosInstance.get(url, {
            responseType: 'blob',
        });
        return {
            data: response.data,
            filename: buildProjectCsvExportFilename(getState, { label: 'RAID', ids: raidItemIds }),
        };
    } catch (error) {
        return rejectWithValue(error.response?.data || 'Export failed');
    }
});

export const PatchRaidItem = createAsyncThunk('PATCH_RAID_ITEM', async ({ id, body, suppressToast }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.patch(RAID_ITEMS + localStorage['project_id'] + '/' + id + '/', body);
        // Attach suppressToast flag so reducer can decide about toasts
        return { ...response, suppressToast };
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const DeleteRaidItem = createAsyncThunk('DELETE_RAID_ITEM', async (id, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.delete(RAID_ITEMS + localStorage['project_id'] + '/' + id + '/');
        return { id, response };
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const UploadRaidCSV = createAsyncThunk('RAID_UPLOAD_CSV_FILE', async (body, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(RAID_ITEMS + localStorage['project_id'] + '/bulk_upload_csv/', body, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response;
    } catch (error) {
        return rejectWithValue(error.response?.data || 'Upload failed');
    }
});

export const BulkUpdateRaidItems = createAsyncThunk('BULK_UPDATE_RAID_ITEMS', async ({ raidItemIds, updates }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.patch(RAID_ITEMS + localStorage['project_id'] + '/bulk_update/', {
            raid_item_ids: raidItemIds,
            updates,
        });
        return response;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const BulkDeleteRaidItems = createAsyncThunk('BULK_DELETE_RAID_ITEMS', async ({ raidItemIds }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.delete(RAID_ITEMS + localStorage['project_id'] + '/bulk_delete/', {
            data: { raid_item_ids: raidItemIds },
        });
        return { raidItemIds, response };
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const GetRaidAISummary = createAsyncThunk('GET_RAID_AI_SUMMARY', async ({ raidItemIds, regenerate = false }, { rejectWithValue }) => {
    try {
        if (!raidItemIds || !raidItemIds.length) {
            return rejectWithValue('RAID item IDs are required');
        }
        const response = await axiosInstance.post(RAID_ITEMS + localStorage['project_id'] + '/ai_summary/', {
            raid_item_ids: raidItemIds,
            regenerate,
        });
        return response.data;
    } catch (error) {
        const message =
            error.response?.data?.error ||
            (typeof error.response?.data === 'string' ? error.response.data : null) ||
            error.message ||
            'Failed to generate RAID AI summary';
        return rejectWithValue(message);
    }
});

// RAID item comments
export const GetRaidItemComments = createAsyncThunk('GET_RAID_ITEM_COMMENTS', async ({ raidItemId, projectId }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(RAID_ITEM_COMMENTS + raidItemId + '/' + projectId + '/');
        return response;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const PostRaidItemComment = createAsyncThunk('POST_RAID_ITEM_COMMENT', async (body, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(RAID_ITEM_COMMENTS + body.raid_item + '/' + localStorage['project_id'] + '/', body);
        return response;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const PatchRaidItemComment = createAsyncThunk('PATCH_RAID_ITEM_COMMENT', async ({ commentId, body }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.patch(`/tools/raid-comments/${commentId}/`, body);
        return response;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const DeleteRaidItemComment = createAsyncThunk('DELETE_RAID_ITEM_COMMENT', async ({ commentId, raidItemId }, { rejectWithValue }) => {
    try {
        await axiosInstance.delete(RAID_ITEM_COMMENTS + raidItemId + '/' + localStorage['project_id'] + '/' + commentId + '/');
        return { commentId, raidItemId };
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

// Project RAID owners (similar to project-task-tags)
export const GetProjectRaidOwners = createAsyncThunk('GET_PROJECT_RAID_OWNERS', async () => {
    const response = await axiosInstance.get(PROJECT_RAID_OWNERS + localStorage['project_id'] + '/');
    return response;
});

export const PostProjectRaidOwner = createAsyncThunk('POST_PROJECT_RAID_OWNER', async (body, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(PROJECT_RAID_OWNERS + localStorage['project_id'] + '/', body);
        return response;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const raidSlice = createSlice({
    name: 'raidSlice',
    initialState: {
        raidItems: [],
        projectRaidOwners: [],
        raidComments: [],
        status: null,
        buttonLoading: false,
        downloadTemplateLoading: false,
        uploadCSVBtnLoading: false,
        exportCSVLoading: false,
        raidSummaryData: null,
        raidSummaryLoading: false,
        raidSummaryError: null,
    },
    reducers: {
        resetRaidData: () => ({
            raidItems: [],
            projectRaidOwners: [],
            raidComments: [],
            status: null,
            buttonLoading: false,
            downloadTemplateLoading: false,
            uploadCSVBtnLoading: false,
            exportCSVLoading: false,
            raidSummaryData: null,
            raidSummaryLoading: false,
            raidSummaryError: null,
        }),
        clearRaidSummary: state => {
            state.raidSummaryData = null;
            state.raidSummaryError = null;
        },
    },
    extraReducers: {
        // GetRaidItems
        [GetRaidItems.pending]: state => {
            state.status = 'loading';
        },
        [GetRaidItems.fulfilled]: (state, { payload }) => {
            state.status = 'success';
            state.raidItems = payload?.data || [];
        },
        [GetRaidItems.rejected]: (state, action) => {
            state.status = 'rejected';
            toast.error('Failed to load RAID items');
        },

        // PostRaidItem
        [PostRaidItem.pending]: state => {
            state.buttonLoading = true;
        },
        [PostRaidItem.fulfilled]: (state, { payload }) => {
            state.buttonLoading = false;
            if (payload?.data) {
                state.raidItems = state.raidItems ? [...state.raidItems, payload.data] : [payload.data];
            }
            toast.success('RAID item created successfully');
        },
        [PostRaidItem.rejected]: (state, action) => {
            state.buttonLoading = false;
            const errors = action.payload;
            if (errors && typeof errors === 'object') {
                Object.keys(errors).forEach(key => {
                    const value = errors[key];
                    if (Array.isArray(value)) {
                        value.forEach(item => toast.error(`${key.toUpperCase()} : ${item}`));
                    } else {
                        toast.error(String(value));
                    }
                });
            } else {
                toast.error('Failed to create RAID item');
            }
        },

        // PatchRaidItem
        [PatchRaidItem.fulfilled]: (state, { payload }) => {
            if (payload?.data) {
                state.raidItems = state.raidItems?.map(item => (item.id === payload.data.id ? payload.data : item));
            }
            if (!payload?.suppressToast) {
                toast.success('RAID item updated successfully');
            }
        },
        [PatchRaidItem.rejected]: state => {
            toast.error('Failed to update RAID item');
        },

        // DeleteRaidItem
        [DeleteRaidItem.fulfilled]: (state, { payload }) => {
            state.raidItems = state.raidItems?.filter(item => item.id !== payload?.id);
            toast.success('RAID item deleted successfully');
        },
        [DeleteRaidItem.rejected]: state => {
            toast.error('Failed to delete RAID item');
        },

        // DownloadRaidTemplate
        [DownloadRaidTemplate.pending]: state => {
            state.downloadTemplateLoading = true;
        },
        [DownloadRaidTemplate.fulfilled]: (state, action) => {
            state.downloadTemplateLoading = false;
            const url = window.URL.createObjectURL(new Blob([action.payload.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', action.payload.filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('RAID template downloaded successfully');
        },
        [DownloadRaidTemplate.rejected]: (state, action) => {
            state.downloadTemplateLoading = false;
            toast.error(action.payload || 'Failed to download RAID template');
        },

        // UploadRaidCSV
        [UploadRaidCSV.pending]: state => {
            state.uploadCSVBtnLoading = true;
        },
        [UploadRaidCSV.fulfilled]: (state, action) => {
            state.uploadCSVBtnLoading = false;
            const createdItems = action.payload?.data?.items || [];
            if (createdItems.length) {
                state.raidItems = state.raidItems ? [...state.raidItems, ...createdItems] : createdItems;
            }
            const created = action.payload?.data?.created ?? createdItems.length;
            const skipped = action.payload?.data?.skipped ?? 0;
            toast.success(`Imported ${created} RAID item${created === 1 ? '' : 's'}${skipped ? `, skipped ${skipped}` : ''}`);
        },
        [UploadRaidCSV.rejected]: (state, action) => {
            state.uploadCSVBtnLoading = false;
            const error = action.payload;
            if (error && typeof error === 'object') {
                Object.keys(error).forEach(key => {
                    const value = error[key];
                    if (Array.isArray(value)) {
                        value.forEach(item => toast.error(`${key.toUpperCase()} : ${item}`));
                    } else {
                        toast.error(String(value));
                    }
                });
            } else {
                toast.error(error || 'Failed to import RAID CSV');
            }
        },

        // BulkUpdateRaidItems
        [BulkUpdateRaidItems.pending]: state => {
            state.buttonLoading = true;
        },
        [BulkUpdateRaidItems.fulfilled]: (state, { payload }) => {
            state.buttonLoading = false;
            const updatedItems = payload?.data?.items || [];
            if (updatedItems.length) {
                const map = new Map(updatedItems.map(item => [item.id, item]));
                state.raidItems = state.raidItems.map(item => (map.has(item.id) ? map.get(item.id) : item));
            }
            const updated = payload?.data?.updated ?? updatedItems.length;
            toast.success(`Updated ${updated} RAID item${updated === 1 ? '' : 's'}`);
        },
        [BulkUpdateRaidItems.rejected]: (state, action) => {
            state.buttonLoading = false;
            toast.error(action.payload || 'Failed to bulk update RAID items');
        },

        // BulkDeleteRaidItems
        [BulkDeleteRaidItems.pending]: state => {
            state.buttonLoading = true;
        },
        [BulkDeleteRaidItems.fulfilled]: (state, { payload }) => {
            state.buttonLoading = false;
            const idsToDelete = new Set(payload?.raidItemIds || []);
            state.raidItems = state.raidItems?.filter(item => !idsToDelete.has(item.id));
            const deleted = payload?.raidItemIds?.length ?? 0;
            toast.success(`Deleted ${deleted} RAID item${deleted === 1 ? '' : 's'}`);
        },
        [BulkDeleteRaidItems.rejected]: (state, action) => {
            state.buttonLoading = false;
            toast.error(action.payload || 'Failed to bulk delete RAID items');
        },

        // ExportRaidCSV
        [ExportRaidCSV.pending]: state => {
            state.exportCSVLoading = true;
        },
        [ExportRaidCSV.fulfilled]: (state, action) => {
            state.exportCSVLoading = false;
            const raidItemIds = action.meta?.arg;
            const successMessage =
                raidItemIds && raidItemIds.length > 0 ? `Successfully exported ${raidItemIds.length} RAID item${raidItemIds.length > 1 ? 's' : ''}` : 'RAID exported successfully';

            const url = window.URL.createObjectURL(new Blob([action.payload.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', action.payload.filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success(successMessage);
        },
        [ExportRaidCSV.rejected]: (state, action) => {
            state.exportCSVLoading = false;
            toast.error(action.payload || 'Failed to export RAID');
        },

        // GetProjectRaidOwners
        [GetProjectRaidOwners.fulfilled]: (state, { payload }) => {
            state.projectRaidOwners = payload?.data || [];
        },
        [GetProjectRaidOwners.rejected]: state => {
            toast.error('Failed to load RAID owners');
        },

        // PostProjectRaidOwner
        [PostProjectRaidOwner.fulfilled]: (state, { payload }) => {
            if (payload?.data) {
                state.projectRaidOwners = state.projectRaidOwners ? [...state.projectRaidOwners, payload.data] : [payload.data];
            }
        },
        [PostProjectRaidOwner.rejected]: (state, action) => {
            const errors = action.payload;
            if (errors && typeof errors === 'object') {
                Object.keys(errors).forEach(key => {
                    const value = errors[key];
                    if (Array.isArray(value)) {
                        value.forEach(item => toast.error(`${key.toUpperCase()} : ${item}`));
                    } else {
                        toast.error(String(value));
                    }
                });
            } else {
                toast.error('Failed to create RAID owner');
            }
        },

        // RAID comments
        [GetRaidItemComments.fulfilled]: (state, { payload }) => {
            state.raidComments = payload?.data || [];
        },
        [GetRaidItemComments.rejected]: state => {
            toast.error('Failed to load RAID comments');
        },

        [PostRaidItemComment.pending]: state => {
            state.buttonLoading = true;
        },
        [PostRaidItemComment.fulfilled]: (state, { payload }) => {
            state.buttonLoading = false;
            if (payload?.data) {
                state.raidComments.push(payload.data);
                const raidItemId = payload.data.raid_item;
                state.raidItems = state.raidItems.map(item => (item.id === raidItemId ? { ...item, comments_count: (item.comments_count || 0) + 1 } : item));
            }
            toast.success('Comment added');
        },
        [PostRaidItemComment.rejected]: (state, action) => {
            state.buttonLoading = false;
            toast.error(action.payload || 'Failed to add RAID comment');
        },

        [PatchRaidItemComment.pending]: state => {
            state.buttonLoading = true;
        },
        [PatchRaidItemComment.fulfilled]: (state, { payload }) => {
            state.buttonLoading = false;
            if (payload?.data) {
                state.raidComments = state.raidComments.map(comment => (comment.id === payload.data.id ? payload.data : comment));
            }
            toast.success('Comment updated');
        },
        [PatchRaidItemComment.rejected]: (state, action) => {
            state.buttonLoading = false;
            toast.error(action.payload || 'Failed to update RAID comment');
        },

        [DeleteRaidItemComment.pending]: state => {
            state.buttonLoading = true;
        },
        [DeleteRaidItemComment.fulfilled]: (state, { payload }) => {
            state.buttonLoading = false;
            state.raidComments = state.raidComments.filter(comment => comment.id !== payload.commentId);
            state.raidItems = state.raidItems.map(item => (item.id === payload.raidItemId ? { ...item, comments_count: Math.max(0, (item.comments_count || 0) - 1) } : item));
            toast.success('Comment deleted');
        },
        [DeleteRaidItemComment.rejected]: (state, action) => {
            state.buttonLoading = false;
            toast.error(action.payload || 'Failed to delete RAID comment');
        },

        // GetRaidAISummary
        [GetRaidAISummary.pending]: state => {
            state.raidSummaryLoading = true;
            state.raidSummaryError = null;
        },
        [GetRaidAISummary.fulfilled]: (state, { payload }) => {
            state.raidSummaryLoading = false;
            state.raidSummaryData = payload;
            state.raidSummaryError = null;
        },
        [GetRaidAISummary.rejected]: (state, { payload }) => {
            state.raidSummaryLoading = false;
            state.raidSummaryData = null;
            state.raidSummaryError = payload;
        },
    },
});

export const { resetRaidData, clearRaidSummary } = raidSlice.actions;
export default raidSlice.reducer;
